import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import MultipleSaleQtyComponent from "@tJohnstonCompanies/Components/MultipleSaleQty";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showPrice: boolean;
    showAddToList: boolean;
    view?: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

const mapDispatchToProps = {};

type Props = ReturnType<typeof mapStateToProps> &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps;

export interface ProductListActionsStyles {
    wrapper?: InjectableCss;
    gridWrapper?: InjectableCss;
    addToCartWrapperCustom?: InjectableCss;
    addToCartButton?: ButtonPresentationProps;
    quantityOrdered?: TextFieldProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    price?: ProductPriceStyles;
    priceGrid?: ProductPriceStyles;
    addToListLink?: ProductAddToListLinkStyles;
    addToListWrapper?: InjectableCss;
    addToCartWrapperCustomGrid?: InjectableCss;
    multipleSaleQty?: InjectableCss;
}

export const actionsStyles: ProductListActionsStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
            background-color: #f0f0f0;
            align-items: flex-end;
            padding: 20px 16px 0px 16px;
            border-radius: 10px;
        `,
    },

    gridWrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
            background-color: #f0f0f0;
            align-items: flex-end;
            padding: 20px 16px 0px 16px;
            border-radius: 0px;
            align-items: center;
            height: 100%;
        `,
    },
    price: {
        price: {
            priceText: {
                css: css`
                    color: #333;
                    font-weight: 800;
                    font-size: 18px;
                `,
            },
            unitOfMeasureText: {
                css: css`
                    font-size: 18px;
                    color: #333;
                    font-weight: 600;
                `,
            },
        },
        packLabelText: {
            css: css`
                color: #888888;
                font-size: 12px;
                font-weight: 600;
            `,
        },
        packDescriptionText: {
            css: css`
                color: #888888;
                font-size: 12px;
                font-weight: 600;
            `,
        },
        packWrapper: {
            css: css`
                margin-top: -6px;
                display: flex;
                justify-content: flex-end;
            `,
        },
    },
    priceGrid: {
        price: {
            priceText: {
                css: css`
                    color: #333;
                    font-weight: 800;
                    font-size: 18px;
                `,
            },
            unitOfMeasureText: {
                css: css`
                    font-size: 18px;
                    color: #333;
                    font-weight: 600;
                `,
            },
        },
        packLabelText: {
            css: css`
                color: #888888;
                font-size: 12px;
                font-weight: 600;
            `,
        },
        packDescriptionText: {
            css: css`
                color: #888888;
                font-size: 12px;
                font-weight: 600;
            `,
        },
        packWrapper: {
            css: css`
                margin-top: -6px;
                display: flex;
                justify-content: center;
            `,
        },
    },
    unitOfMeasureSelect: {
        cssOverrides: {
            formField: css`
                margin-top: 20px;
                border: 1px solid red;
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: {
            css: css`
                margin: 8px 0;
            `,
        },
    },
    addToCartWrapperCustom: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            margin: 12px 0;
            margin-top: 35px;
            .multipleSaleQty {
                bottom: 0px;
                left: 5px;
            }
        `,
    },
    addToCartWrapperCustomGrid: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            padding: 30px;
            margin-top: auto;
            .multipleSaleQty {
                position: absolute;
                bottom: 0px;
                left: 5px;
            }
        `,
    },
    addToCartButton: {
        css: css`
            padding: 0 21px;
            color: #ffffff;
            border-radius: 10px;
            margin: 8px 0;
            background-color: #333;
            border: #333;
            min-height: 36px;
            width: 100%;
            &:hover {
                background-color: #333;
                border: #333;
            }
            > span {
                font-weight: 600;
            }
        `,
    },
    addToListWrapper: {
        css: css`
            margin-top: 20px;
            min-height: 2rem;
        `,
    },
    multipleSaleQty: {
        css: css`
            position: inherit;
        `,
    },
};

const styles = actionsStyles;

const ProductListActions: FC<Props> = ({ productContext, productSettings, showPrice, showAddToList, view }) => {
    const [discontinued, setDiscontinued] = React.useState("");

    React.useEffect(() => {
        setDiscontinued(productContext.product?.properties?.productDiscontinued);
    }, [productContext]);

    return (
        <>
            {discontinued !== "true" ? (
                <StyledWrapper {...(view === "grid" ? { ...styles.gridWrapper } : { ...styles.wrapper })}>
                    <StyledWrapper id="priceSection">
                        {showPrice && (
                            <>
                                <ProductPrice
                                    product={productContext}
                                    showLabel={false}
                                    showSavings={true}
                                    showSavingsAmount={productSettings.showSavingsAmount}
                                    showSavingsPercent={productSettings.showSavingsPercent}
                                    extendedStyles={view === "grid" ? styles.priceGrid : styles.price}
                                />

                                <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                            </>
                        )}
                        <ProductUnitOfMeasureSelect extendedStyles={styles.unitOfMeasureSelect} />
                    </StyledWrapper>
                    <StyledWrapper
                        id="addToCartWrapper"
                        {...(view === "grid"
                            ? { ...styles.addToCartWrapperCustomGrid }
                            : { ...styles.addToCartWrapperCustom })}
                    >
                        <StyledWrapper id="qtyWrapper">
                            <ProductQuantityOrdered extendedStyles={styles.quantityOrdered} />
                        </StyledWrapper>
                        <StyledWrapper id="actionsAddToCart">
                            <ProductAddToCartButton
                                data-test-selector={`actionsAddToCart${productContext.product.id}`}
                                extendedStyles={styles.addToCartButton}
                            />
                            <StyledWrapper {...styles.multipleSaleQty}>
                                {productContext.product.detail?.multipleSaleQty
                                    ? productContext.product.detail?.multipleSaleQty > 1 && (
                                          <MultipleSaleQtyComponent
                                              multipleSaleQty={productContext.product.detail?.multipleSaleQty}
                                          ></MultipleSaleQtyComponent>
                                      )
                                    : null}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                    {/* {showAddToList && (
   <StyledWrapper {...styles.addToListWrapper}>
       <ProductAddToListLink
           data-test-selector={`actionsAddToList${productContext.product.id}`}
           extendedStyles={styles.addToListLink}
       />
   </StyledWrapper>
)} */}
                </StyledWrapper>
            ) : null}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(ProductListActions));
